/**
 * Survey Export Tab Component
 * Shows survey results in MUI DataGrid with CSV export
 */

import { useMemo } from 'react'
import { Box, Typography, Alert, Skeleton } from '@mui/material'
import { Poll as SurveyIcon } from '@mui/icons-material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import { sortBy } from 'ramda'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { useSurveyData, ParsedSurveyItem } from '../../hooks/use-survey-data'

interface SurveyExportTabProps {
  linkedServiceID: string
}

/**
 * Custom DataGrid toolbar for survey export
 */
function CustomToolbar() {
  return <DataGridToolbar fileName="survey-results" />
}

export default function SurveyExportTab({
  linkedServiceID,
}: SurveyExportTabProps) {
  // Fetch survey data
  let {
    data: surveyData = [],
    isLoading,
    error,
  } = useSurveyData(linkedServiceID, true)

  // Sort by date
  let sortedData = useMemo(
    () =>
      sortBy(
        (survey) => new Date(survey.date).getTime(),
        surveyData,
      ) as (ParsedSurveyItem & { id: string })[],
    [surveyData],
  )

  // Generate dynamic columns based on survey data
  let columns: GridColDef[] = useMemo(() => {
    if (sortedData.length === 0) return []

    // Get all unique keys from all survey items
    let allKeys = sortedData.reduce<string[]>((keys, survey) => {
      let newKeys = Object.keys(survey).filter((key) => !keys.includes(key))
      return [...keys, ...newKeys]
    }, [])

    // Order: name, then all other fields, then date
    let middleKeys = allKeys.filter(
      (k) => k !== 'name' && k !== 'date' && k !== 'id',
    )
    let orderedKeys = ['name', ...middleKeys, 'date']

    // Create columns
    return orderedKeys.map((key) => {
      let baseColumn: GridColDef = {
        field: key,
        headerName:
          key.charAt(0).toUpperCase() +
          key
            .slice(1)
            .replace(/([A-Z])/g, ' $1')
            .trim(),
        width: 200,
        flex: key === 'name' ? 1 : undefined,
        minWidth: key === 'name' ? 200 : undefined,
      }

      // Special handling for name field
      if (key === 'name') {
        return {
          ...baseColumn,
          headerName: 'Course / Event Name',
        }
      }

      // Special handling for date field
      if (key === 'date') {
        return {
          ...baseColumn,
          headerName: 'Date',
          width: 180,
          flex: undefined,
          minWidth: undefined,
          renderCell: (params) => {
            if (!params.value) return '-'
            return dayjs(params.value).format('MMM D, YYYY h:mm A')
          },
        }
      }

      // For other fields, handle various data types
      return {
        ...baseColumn,
        renderCell: (params) => {
          if (params.value === null || params.value === undefined) return '-'

          // Handle objects/arrays by converting to JSON string
          if (typeof params.value === 'object') {
            return JSON.stringify(params.value)
          }

          return String(params.value)
        },
      }
    })
  }, [sortedData])

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load survey data: {error.message}
      </Alert>
    )
  }

  if (isLoading) {
    return <Skeleton variant="rectangular" height={600} />
  }

  if (!surveyData || surveyData.length === 0) {
    return (
      <Box
        sx={{
          height: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'text.secondary',
        }}
      >
        <SurveyIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          No Survey Data
        </Typography>
        <Typography variant="body2">
          No survey responses found for this linked service.
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {sortedData.length} survey response{sortedData.length !== 1 ? 's' : ''}
      </Typography>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGridPremium
          rows={sortedData}
          columns={columns}
          loading={isLoading}
          slots={{
            toolbar: CustomToolbar,
          }}
          showToolbar
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
            sorting: {
              sortModel: [{ field: 'date', sort: 'desc' }],
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-toolbarContainer': {
              borderBottom: '1px solid',
              borderColor: 'divider',
              p: 2,
            },
          }}
        />
      </Box>
    </>
  )
}
